/**
 * RequireAuth — wrap any subtree to gate it behind a logged-in session.
 *
 * Behavior:
 *   1. Reads the session via `authSession`, which fetches `/api/auth/me` once
 *      per document and shares it across every caller. When the answer is
 *      already known the subtree renders on the first frame; only the very
 *      first check can wait.
 *   2. On 401 / network error / missing user, hard-navigates to
 *      `/login?redirect=<currentPath>` so the user lands back here after
 *      signing in. We use `window.location.replace` (not React Router) so the
 *      login page mounts fresh — Login itself doesn't need any of the authed
 *      providers/state.
 *
 * It used to fetch `/api/auth/me` itself on every mount, which meant a blank
 * screen for a full round trip on each guarded navigation — worst on a slow
 * connection, where it was seconds of nothing between pages.
 *
 * Difference from AuthRoute:
 *   - AuthRoute uses a render-prop `children: (user) => ReactNode` and is
 *     used by dashboard pages that need the user object (e.g. AppsList).
 *   - RequireAuth just gates a subtree. No user is exposed. Use this for
 *     wrapping whole routes (editor, embedded, etc.) where the rendered
 *     tree gets the viewer through Redux instead.
 */
import React, { ReactNode, useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { getSession, peekSession } from '@/features/auth/session/authSession';

interface Props extends RouteComponentProps {
  children: ReactNode;
}

function RequireAuthInner({ children, location }: Props) {
  const known = peekSession();
  const [authed, setAuthed] = useState<boolean | null>(known === undefined ? null : Boolean(known));

  useEffect(() => {
    let cancelled = false;
    const bounce = () => {
      const target = encodeURIComponent(`${location.pathname}${location.search}`);
      window.location.replace(`/login?redirect=${target}`);
    };

    if (known !== undefined) {
      if (known === null) bounce();
      return undefined;
    }
    getSession().then((s) => {
      if (cancelled) return;
      if (s) setAuthed(true);
      else bounce();
    });
    return () => {
      cancelled = true;
    };
    // Only re-check when the route itself changes — query-string changes
    // (e.g. typing in the URL bar) shouldn't re-fire the auth probe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [known, location.pathname]);

  if (!authed) return null;
  return <>{children}</>;
}

export default withRouter(RequireAuthInner);
